import { useEffect, useRef, useState } from 'react';
import { MACHINE_RITES } from '../../../data/MACHINE_RITES.js';

/**
 * phase:
 * - "boot"      : random rites appear letter-by-letter (fast)
 * - "cooldown"  : stop boot typing, type ">> LOADING COMPLETE" as a feed line,
 *                 pause, then drain everything (retract style)
 */

export default function LoadingScreen({
  show = true,
  phase = 'boot',
  onPhase2Ready,
}) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const timerRef = useRef(null);

  const phaseTimerRef = useRef(null);
  const nextLineTimerRef = useRef(null);
  const charTimerRef = useRef(null);
  const drainIntervalRef = useRef(null);

  const typingActiveRef = useRef(false);
  const completeTypedRef = useRef(false);

  const [logLines, setLogLines] = useState([]);
  const START_DELAY_MS = 500;
  const HOLD_AFTER_COMPLETE_MS = 100;

  const DRAIN_TICK_MS = 90;
  const DRAIN_MIN_MS = 500;
  const DRAIN_MAX_MS = 1000;

  /* =========================
     STATIC NOISE (CANVAS)
     ========================= */
  useEffect(() => {
    if (!show) return;

    const c = canvasRef.current;
    if (!c) return;

    const ctx = c.getContext('2d', { alpha: true });
    if (!ctx) return;

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      const w = window.innerWidth;
      const h = window.innerHeight;

      c.style.width = `${w}px`;
      c.style.height = `${h}px`;

      c.width = Math.floor(w * dpr);
      c.height = Math.floor(h * dpr);

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    resize();
    window.addEventListener('resize', resize);

    let running = true;

    const draw = () => {
      if (!running) return;

      const w = window.innerWidth;
      const h = window.innerHeight;

      const img = ctx.createImageData(w, h);
      const d = img.data;

      for (let i = 0; i < d.length; i += 4) {
        if (Math.random() < 0.06) {
          const v = (Math.random() * 255) | 0;
          d[i] = v;
          d[i + 1] = v;
          d[i + 2] = v;
          d[i + 3] = 18;
        } else {
          d[i + 3] = 0;
        }
      }

      ctx.putImageData(img, 0, 0);

      timerRef.current = window.setTimeout(() => {
        rafRef.current = requestAnimationFrame(draw);
      }, 80);
    };

    draw();

    return () => {
      running = false;
      window.removeEventListener('resize', resize);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [show]);

  /* =========================
     TIMER HELPERS
     ========================= */
  const stopPhaseTimer = () => {
    if (phaseTimerRef.current) {
      clearTimeout(phaseTimerRef.current);
      phaseTimerRef.current = null;
    }
  };

  const stopNextLineTimer = () => {
    if (nextLineTimerRef.current) {
      clearTimeout(nextLineTimerRef.current);
      nextLineTimerRef.current = null;
    }
  };

  const stopCharTimer = () => {
    if (charTimerRef.current) {
      clearTimeout(charTimerRef.current);
      charTimerRef.current = null;
    }
  };

  const stopDrain = () => {
    if (drainIntervalRef.current) {
      clearInterval(drainIntervalRef.current);
      drainIntervalRef.current = null;
    }
  };

  const stopAllTyping = () => {
    typingActiveRef.current = false;
    stopCharTimer();
    stopNextLineTimer();
  };

  const pickLine = () =>
    MACHINE_RITES[Math.floor(Math.random() * MACHINE_RITES.length)];

  /* =========================
     FAST LETTER-BY-LETTER LINE TYPING
     ========================= */
  const typeOneLine = (fullText, onDone) => {
    // add new empty line to the feed
    setLogLines((prev) => {
      const next = [...prev, ''];
      if (next.length > 22) next.shift();
      return next;
    });

    let idx = 0;

    const step = () => {
      if (!typingActiveRef.current) return;

      idx += 1;

      setLogLines((prev) => {
        if (prev.length === 0) return prev;
        const next = [...prev];
        const last = next.length - 1;
        next[last] = fullText.slice(0, idx);
        return next;
      });

      if (idx >= fullText.length) {
        charTimerRef.current = window.setTimeout(() => {
          if (!typingActiveRef.current) return;
          onDone?.();
        }, 40);
        return;
      }

      const nextDelay = 10 + Math.random() * 16; // fast typing
      charTimerRef.current = window.setTimeout(step, nextDelay);
    };

    step();
  };

  /* =========================
     STAGE: BOOT
     ========================= */
  useEffect(() => {
    if (!show) return;
    if (phase !== 'boot') return;

    // reset
    completeTypedRef.current = false;

    // IMPORTANT: stop old timers FIRST, then enable typing
    stopDrain();
    stopPhaseTimer();
    stopAllTyping();

    typingActiveRef.current = true;
    setLogLines([]);

    const loop = () => {
      if (!typingActiveRef.current) return;

      const line = pickLine();

      typeOneLine(line, () => {
        if (!typingActiveRef.current) return;

        const nextDelay = 160 + Math.random() * 500; // prevents bursts
        nextLineTimerRef.current = window.setTimeout(loop, nextDelay);
      });
    };

    phaseTimerRef.current = window.setTimeout(() => {
      loop();
    }, START_DELAY_MS);

    return () => {
      stopAllTyping();
      stopPhaseTimer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, phase]);

  /* =========================
     STAGE: COOLDOWN
     ========================= */
  useEffect(() => {
    if (!show) return;
    if (phase !== 'cooldown') return;

    // stop boot typing immediately
    stopAllTyping();
    stopDrain();
    stopPhaseTimer();

    // now we will type the completion line, so re-enable typing
    typingActiveRef.current = true;

    const beginDrain = () => {
      typingActiveRef.current = false; // no more typing while draining

      const drainStart = Date.now();
      const drainTarget =
        DRAIN_MIN_MS + Math.random() * (DRAIN_MAX_MS - DRAIN_MIN_MS);

      drainIntervalRef.current = setInterval(() => {
        setLogLines((prev) => {
          if (prev.length === 0) return prev;
          return prev.slice(1);
        });
      }, DRAIN_TICK_MS);

      const checkDone = () => {
        const elapsed = Date.now() - drainStart;

        setLogLines((prev) => {
          const empty = prev.length === 0;

          if (empty && elapsed >= drainTarget) {
            stopDrain();
            stopPhaseTimer();

            phaseTimerRef.current = window.setTimeout(() => {
              onPhase2Ready?.();
            }, 250);
          } else {
            stopPhaseTimer();
            phaseTimerRef.current = window.setTimeout(checkDone, 120);
          }

          return prev;
        });
      };

      checkDone();
    };

    const typeCompleteThenDrain = () => {
      if (completeTypedRef.current) {
        // if we somehow re-enter cooldown, just proceed to drain flow
        phaseTimerRef.current = window.setTimeout(
          beginDrain,
          HOLD_AFTER_COMPLETE_MS
        );
        return;
      }

      completeTypedRef.current = true;

      typeOneLine('>> LOADING COMPLETE', () => {
        // hold, then retract everything
        phaseTimerRef.current = window.setTimeout(
          beginDrain,
          HOLD_AFTER_COMPLETE_MS
        );
      });
    };

    typeCompleteThenDrain();

    return () => {
      stopAllTyping();
      stopDrain();
      stopPhaseTimer();
    };
  }, [show, phase, onPhase2Ready]);

  useEffect(() => {
    return () => {
      stopAllTyping();
      stopDrain();
      stopPhaseTimer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!show) return null;

  const lastIndex = logLines.length - 1;

  return (
    <div className="loadingScreen" role="status" aria-live="polite">
      <canvas ref={canvasRef} className="loadingNoise" aria-hidden="true" />

      <div className="loadingCenter">
        <div className="loadingTerminal">
          {logLines.map((line, i) => {
            const isLast = i === lastIndex;
            return (
              <div key={i} className="terminalLine">
                {line}
                {phase === 'boot' && isLast && (
                  <span className="terminalCursor">▮</span>
                )}
              </div>
            );
          })}

          {/* fallback: if no lines yet in boot, show cursor */}
          {logLines.length === 0 && phase === 'boot' && (
            <div className="terminalLine">
              <span className="terminalCursor">▮</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
